import React, {MouseEventHandler, useContext, useEffect} from "react";
import {GenericComponentModel, SelectElementModelBE, SelectElementModelFE,} from "../../../api/types/generic";
import {useRecoilState} from "recoil";
import {ancillaryListAtom} from "../../../state/ancillariesRecoil";
import {useTranslation} from "react-i18next";
import SearchFilters from "../../../common/components/SearchFilters";
import {commercialCategoryListAtom} from "../../../state/commercialCategoriesRecoil";
import {commercialSegmentListAtom} from "../../../state/commercialSegmentsAtom";
import {useCommercialCategoryRepository} from "../../../api/repositories/CommercialCategoryRepository";
import {fromGenericBeToGenericFe} from "../../../utils";
import {CommercialCategoryEntityResponse, CommercialCategorySelectFE,} from "../../../api/types/commercialCategory";
import {owAncillaryExceptionFiltersAtom} from "../../../state/owAncillaryExceptionRecoil";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";

const OWAncillaryExceptionFilters = ({
                                         handleClickSearch,
                                         handleClickResetSearch,
                                         commercialCategoryListOriginal,
                                         testFilters,
                                         setTestFilters
                                     }: {
    handleClickSearch: MouseEventHandler<HTMLButtonElement>
    handleClickResetSearch: MouseEventHandler<HTMLButtonElement>
    commercialCategoryListOriginal: CommercialCategorySelectFE[]
    testFilters: any
    setTestFilters: any
}) => {
    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Recoil
    const [owAncillaryExceptionFilters, setOWAncillaryExceptionFilters] = useRecoilState(owAncillaryExceptionFiltersAtom);
    const [commercialCategoryList, setCommercialCategoryList] = useRecoilState(commercialCategoryListAtom);
    const [commercialSegmentList] = useRecoilState(commercialSegmentListAtom);
    const [exceptionList] = useRecoilState(ancillaryListAtom);

    // Use Context
    const {globalData: {user},} = useContext(GlobalContext);

    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    const dealerOptions: SelectElementModelFE[] = user.dealers.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    // Filtra commercial categories quando cambiano legal entities o commercial segment
    useEffect(() => {
        // Ottieni le legal entities selezionate, con fallback per mono-tenant
        let selectedLegalEntities: string[];
        if (testFilters.legalEntities && 
            (!Array.isArray(testFilters.legalEntities) || testFilters.legalEntities.length > 0)) {
            selectedLegalEntities = Array.isArray(testFilters.legalEntities)
                ? testFilters.legalEntities.map((le: SelectElementModelFE) => le.code as string)
                : [testFilters.legalEntities.code as string];
        } else if (user.legalEntities?.length === 1) {
            selectedLegalEntities = [user.legalEntities[0].name];
        } else {
            setCommercialCategoryList([]);
            return;
        }

        // Filtra per commercial segment se selezionato
        let filteredBySegment = commercialCategoryListOriginal;
        if (testFilters.commercialSegmentId) {
            filteredBySegment = commercialCategoryListOriginal.filter(
                cc => cc.commercialSegment.id === (testFilters.commercialSegmentId as SelectElementModelFE).code
            );
        }

        // Espandi le categorie in base alle legal entities selezionate e deduplica per label
        const expandedCategories = new Map<string, CommercialCategorySelectFE>();
        
        filteredBySegment.forEach(category => {
            category.labels?.forEach(labelObj => {
                if (selectedLegalEntities.includes(labelObj.country)) {
                    const key = labelObj.label;
                    if (!expandedCategories.has(key)) {
                        expandedCategories.set(key, {
                            ...category,
                            label: labelObj.label
                        });
                    }
                }
            });
        });

        setCommercialCategoryList(Array.from(expandedCategories.values()));
    }, [testFilters.legalEntities, testFilters.commercialSegmentId, commercialCategoryListOriginal]);


    const filtersOutOfCollapse: GenericComponentModel[] = [
        {
            name: "name",
            value: testFilters.name,
            label: `${t("pricing:ancillaryException.form.labels.name")}`,
            handleChange: (value: string) => {
                setTestFilters({
                    ...testFilters,
                    name: value
                });
            },
            options: [],
            type: "TextField"
        },
        {
            name: "commercialSegmentId",
            value: testFilters.commercialSegmentId,
            label: `${t("pricing:ancillaryException.form.labels.commercialSegment")}`,
            handleChange: (value: SelectElementModelFE) => {
                setTestFilters({
                    ...testFilters,
                    commercialSegmentId: value,
                    commercialCategoryId: null
                });
            },
            options: commercialSegmentList,
            type: "Autocomplete"
        },
        {
            name: "commercialCategoryId",
            value: testFilters.commercialCategoryId,
            label: `${t("pricing:ancillaryException.form.labels.commercialCategory")}`,
            handleChange: (value: CommercialCategorySelectFE) => {
                setTestFilters({
                    ...testFilters,
                    commercialCategoryId: value
                });
            },
            options: commercialCategoryList,
            type: "Autocomplete",
            disabled: !testFilters.legalEntities || 
                     (Array.isArray(testFilters.legalEntities) && testFilters.legalEntities.length === 0),
        }
    ];
    const filtersInCollapse: GenericComponentModel[] = [
        {
            name: "reservationFrom",
            value: testFilters.reservationFrom as string,
            label: `${t("pricing:ancillaryException.form.labels.reservationFrom")}`,
            handleChange: (value: string) => setTestFilters({
                ...testFilters,
                reservationFrom: value
            }),
            options: [],
            type: "Date"
        },
        {
            name: "reservationTo",
            value: testFilters.reservationTo as string,
            label: `${t("pricing:ancillaryException.form.labels.reservationTo")}`,
            handleChange: (value: string) => setTestFilters({
                ...testFilters,
                reservationTo: value
            }),
            options: [],
            type: "Date"
        },
        {
            name: "pickupDateFrom",
            value: testFilters.pickupDateFrom as string,
            label: `${t("pricing:ancillaryException.form.labels.pickupDateFrom")}`,
            handleChange: (value: string) => setTestFilters({
                ...testFilters,
                pickupDateFrom: value
            }),
            options: [],
            type: "Date"
        },
        {
            name: "pickupDateTo",
            value: testFilters.pickupDateTo as string,
            label: `${t("pricing:ancillaryException.form.labels.pickupDateTo")}`,
            handleChange: (value: string) => setTestFilters({
                ...testFilters,
                pickupDateTo: value
            }),
            options: [],
            type: "Date"
        },
        {
            name: "ancillaryId",
            value: testFilters.ancillaryId,
            label: `${t("pricing:ancillaryException.form.labels.ancillary")}`,
            handleChange: (value: SelectElementModelFE) => {
                setTestFilters({
                    ...testFilters,
                    ancillaryId: value
                });
            },
            options: exceptionList,
            type: "Autocomplete"
        },
        {
            name: "legalEntities",
            value: testFilters.legalEntities as any, // Cast per supportare array
            label: `${t("pricing:ancillaryException.form.labels.legalEntity")}*`,
            handleChange: (value: SelectElementModelFE[]) =>
                setTestFilters({
                    ...testFilters,
                    legalEntities: value,
                    commercialCategoryId: null,
                }),
            options: legalEntityOptions || [],
            type: "AutocompleteM",
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false,
            error: !testFilters.legalEntities || 
                   (Array.isArray(testFilters.legalEntities) && testFilters.legalEntities.length === 0),
            helperText: (!testFilters.legalEntities || 
                        (Array.isArray(testFilters.legalEntities) && testFilters.legalEntities.length === 0))
                ? t("translation:validation.selectAtLeastOne")
                : undefined,
        },
        {
            name: "dealers",
            value: testFilters.dealers as SelectElementModelFE,
            label: `${t("pricing:ancillaryException.form.labels.dealer")}`,
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({
                    ...testFilters,
                    dealers: value,
                }),
            options: dealerOptions || [],
            type: "AutocompleteM",
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        }
    ];

    return (
        // <CardContainer>
        <SearchFilters
            filtersOutOfCollapse={filtersOutOfCollapse}
            filtersInCollapse={filtersInCollapse}
            handleClickSearch={handleClickSearch}
            handleClickResetSearch={handleClickResetSearch}
        />
        // </CardContainer>
    );
};

export default OWAncillaryExceptionFilters;
